import { useCallback, useEffect, useState } from 'react';
import { CAPABILITIES } from '@lemontaigu/platform';
import { useCapability } from '@lemontaigu/platform-ui';
import type {
  FiscalDocument,
  Folio,
  FolioPosition,
  InvoicingService,
  Payment,
  PaymentService,
  PaymentTender,
  ReservationService,
  ReservationView,
} from '@lemontaigu/pms-core';
import { MOVEMENT_FR, TENDER_FR } from './fiscalAuditView.js';
import { fmtDate, fmtEuros, parseEurosToCents } from './logic.js';

// OB6 (PMS-OWNED-BOOKINGS-BRIEF §7.3) — « Note »: a booking the PMS owns, its
// folios with what each stands at, its payments and its factures, and the
// desk's acts on them: take a payment, invoice, issue an avoir, undo a mistaken
// cash-type receipt, keep a deposit. Every act is a database RPC that creates a
// NEW record; nothing here edits one. Until the établissement is live they all
// land on its école caisse (« mode école », outside every total).
//
// A card is never typed (G3): it enters through the terminal. The cash-type
// tenders are offered here; the card stays Encaissement's.

type CashTender = Exclude<PaymentTender, 'card' | 'amex'>;
const CASH_TENDERS: CashTender[] = ['cash', 'cheque', 'ancv', 'bank_transfer', 'vad'];

interface FolioState {
  folio: Folio;
  position: FolioPosition | null;
  payments: Payment[];
  documents: FiscalDocument[];
}

export function FolioDialog({
  booking,
  reservations,
  invoicing,
  payments,
  onClose,
  onChanged,
}: {
  booking: ReservationView;
  reservations: Pick<ReservationService, 'folios'>;
  invoicing: InvoicingService;
  payments: PaymentService;
  onClose: () => void;
  onChanged: () => void;
}) {
  const canPay = useCapability(CAPABILITIES.PMS_PAYMENT_WRITE);
  const canInvoice = useCapability(CAPABILITIES.PMS_INVOICE_WRITE);
  const canCredit = useCapability(CAPABILITIES.PMS_INVOICE_CREDIT);
  const canForfeit = useCapability(CAPABILITIES.PMS_PAYMENT_FORFEIT);
  const [state, setState] = useState<FolioState[] | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [tender, setTender] = useState<CashTender>('cash');
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('');
  const [folioId, setFolioId] = useState<number | null>(null);

  const load = useCallback(async () => {
    const f = await reservations.folios(booking.id);
    if (!f.ok) return setErr(f.error.message);
    const rows = await Promise.all(
      f.value.map(async (folio): Promise<FolioState> => {
        const [pos, pay, docs] = await Promise.all([
          payments.position(folio.id),
          payments.paymentsForFolio(folio.id),
          invoicing.documentsForFolio(folio.id),
        ]);
        if (!pos.ok) setErr(pos.error.message);
        return {
          folio,
          position: pos.ok ? pos.value : null,
          payments: pay.ok ? pay.value : [],
          documents: docs.ok ? docs.value : [],
        };
      }),
    );
    setState(rows);
    setFolioId((cur) => cur ?? rows[0]?.folio.id ?? null);
  }, [booking.id, reservations, payments, invoicing]);

  useEffect(() => {
    void load();
  }, [load]);

  const act = async (run: () => Promise<{ ok: boolean; error?: { message: string } }>) => {
    setBusy(true);
    setErr('');
    const r = await run();
    setBusy(false);
    if (!r.ok) return setErr(r.error?.message ?? 'Erreur');
    await load();
    onChanged();
  };

  const receive = () => {
    const cents = parseEurosToCents(amount);
    if (folioId === null || cents === null || cents <= 0) return setErr('Montant : un nombre positif, en euros.');
    void act(async () => {
      const r = await payments.receive(folioId, {
        tender,
        amountCents: cents,
        ...(ref.trim() ? { receiptRef: ref.trim() } : {}),
      });
      if (r.ok) {
        setAmount('');
        setRef('');
      }
      return r;
    });
  };

  const withReason = (label: string, run: (reason: string) => Promise<{ ok: boolean; error?: { message: string } }>) => {
    const typed = window.prompt(`${label}\n\nMotif (obligatoire) :`, '');
    if (typed === null) return;
    if (typed.trim() === '') return setErr(`${label} : un motif est obligatoire.`);
    void act(() => run(typed.trim()));
  };

  const who = `${(booking.guest?.lastName ?? '?').toUpperCase()} ${booking.guest?.firstName ?? ''}`.trim();

  return (
    <div className="pms-overlay" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="pms-overlay-panel" role="dialog" aria-modal="true" aria-label={`Note — ${who}`}>
        <div className="pms-form pms-folio">
          <h3>
            Note — {who}
            <span className="pms-sub">
              {' '}
              · {fmtDate(booking.checkinDate)} → {fmtDate(booking.checkoutDate)}
            </span>
          </h3>
          {err && <p className="auth-err">Erreur : {err}</p>}
          {state === null && <p className="pms-sub">Lecture de la note…</p>}
          {state !== null && state.length === 0 && (
            <p className="pms-sub">Aucune note : enregistrez d’abord la réservation avec ses prestations.</p>
          )}

          {state?.map((s) => (
            <section key={s.folio.id} className="pms-folio-block">
              <h4>
                <label>
                  {state.length > 1 && (
                    <input
                      type="radio"
                      name="pms-folio"
                      checked={folioId === s.folio.id}
                      onChange={() => setFolioId(s.folio.id)}
                    />
                  )}{' '}
                  {s.folio.labelFr}
                  {s.folio.payerName ? ` — ${s.folio.payerName}` : ''}
                </label>
              </h4>
              {s.position && (
                <dl className="pms-cancel-sum">
                  <dt>À facturer</dt>
                  <dd>{fmtEuros(s.position.unbilledCents)}</dd>
                  <dt>Facturé, non réglé</dt>
                  <dd>{fmtEuros(s.position.invoicedOpenCents)}</dd>
                  <dt>Reçu, non affecté</dt>
                  <dd>{fmtEuros(s.position.unappliedCents)}</dd>
                </dl>
              )}

              <table className="pms-table">
                <thead>
                  <tr>
                    <th>Règlement</th>
                    <th>Date</th>
                    <th className="pms-num">Montant</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {s.payments.length === 0 && (
                    <tr>
                      <td colSpan={4} className="pms-sub">
                        Aucun règlement.
                      </td>
                    </tr>
                  )}
                  {s.payments.map((p) => (
                    <tr key={p.id}>
                      <td>
                        {p.tender ? (TENDER_FR[p.tender] ?? p.tender) : '—'} — {MOVEMENT_FR[p.movement] ?? p.movement}
                        {p.receiptRef ? <div className="pms-sub">Réf. {p.receiptRef}</div> : null}
                        {p.reason ? <div className="pms-sub">{p.reason}</div> : null}
                      </td>
                      <td>{fmtDate(p.authorisedAt.slice(0, 10))}</td>
                      <td className="pms-num">{fmtEuros(p.amountCents)}</td>
                      <td className="pms-actions">
                        {p.movement === 'receipt' && canPay && p.terminalTransactionId === null && (
                          <button
                            type="button"
                            className="pms-mini"
                            disabled={busy}
                            onClick={() => withReason('Annuler cet encaissement', (r) => payments.reverse(p.id, r))}
                          >
                            Annuler
                          </button>
                        )}
                        {p.movement === 'receipt' && canForfeit && (
                          <button
                            type="button"
                            className="pms-mini"
                            disabled={busy}
                            title="Garder les arrhes (annulation, no-show) : sans facture, la TVA déclarée est reprise"
                            onClick={() => withReason('Conserver les arrhes', (r) => payments.forfeit(p.id, r))}
                          >
                            Conserver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <table className="pms-table">
                <thead>
                  <tr>
                    <th>Pièce</th>
                    <th>Date</th>
                    <th className="pms-num">Total TTC</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {s.documents.length === 0 && (
                    <tr>
                      <td colSpan={4} className="pms-sub">
                        Aucune facture.
                      </td>
                    </tr>
                  )}
                  {s.documents.map((d) => (
                    <tr key={d.id}>
                      <td>
                        {d.kind === 'invoice' ? 'Facture' : 'Avoir'} {d.series}-{d.number}
                        {d.isTraining ? <span className="pms-tag">École</span> : null}
                      </td>
                      <td>{fmtDate(d.issuedAt.slice(0, 10))}</td>
                      <td className="pms-num">{fmtEuros(d.totalTtcCents)}</td>
                      <td className="pms-actions">
                        {d.kind === 'invoice' &&
                          canCredit &&
                          !s.documents.some((x) => x.creditsDocumentId === d.id) && (
                            <button
                              type="button"
                              className="pms-mini"
                              disabled={busy}
                              onClick={() =>
                                window.confirm(`Émettre un avoir sur la facture ${d.series}-${d.number} ?`) &&
                                void act(() => invoicing.issueCreditNote(d.id))
                              }
                            >
                              Avoir
                            </button>
                          )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {canInvoice && (s.position?.unbilledCents ?? 0) !== 0 && (
                <button
                  type="button"
                  className="auth-btn"
                  disabled={busy}
                  onClick={() =>
                    window.confirm(`Facturer ${fmtEuros(s.position?.unbilledCents ?? 0)} sur « ${s.folio.labelFr} » ?`) &&
                    void act(() => invoicing.issueInvoice(s.folio.id))
                  }
                >
                  Facturer
                </button>
              )}
            </section>
          ))}

          {canPay && state !== null && state.length > 0 && (
            <div className="pms-folio-pay">
              <strong>Encaisser</strong>
              <select value={tender} onChange={(e) => setTender(e.target.value as CashTender)} aria-label="Mode de règlement">
                {CASH_TENDERS.map((t) => (
                  <option key={t} value={t}>
                    {TENDER_FR[t]}
                  </option>
                ))}
              </select>
              <input
                aria-label="Montant"
                className="pms-num"
                inputMode="decimal"
                placeholder="Montant €"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <input aria-label="Référence" placeholder="Réf. (facultatif)" value={ref} onChange={(e) => setRef(e.target.value)} />
              <button type="button" className="auth-btn" disabled={busy || folioId === null} onClick={receive}>
                Encaisser
              </button>
              <span className="pms-sub">La carte se passe au terminal, depuis Encaissement.</span>
            </div>
          )}

          <div className="pms-form-actions">
            <button type="button" className="auth-btn" onClick={onClose} disabled={busy}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
